import { createEntityAdapter, EntityState } from '@reduxjs/toolkit'
import {
  BaseQueryFn,
  EndpointBuilder,
  FetchArgs,
  FetchBaseQueryError,
  MutationDefinition,
  QueryDefinition
} from '@reduxjs/toolkit/query'
export const ENTITY_TYPES = [
  'users',
  'organisations',
  'matches',
  'teams',
  'teampersonsessions',
  'hardwaresessions',
  'venues',
  'competitions',
  'people',
  'teampeople',
  'teamsessions',
  'events',
  'pitches',
  'status'
] as const

type TagTypes = (typeof ENTITY_TYPES)[number]

type CrudEndpoints<
  Data extends { id: string | number },
  TagType extends string
> = {
  [K in `${TagType}_getAll`]: QueryDefinition<
    { organisationId?: string } | void,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    TagType,
    EntityState<Data, Data['id']>
  >
} & {
  [K in `${TagType}_getById`]: QueryDefinition<
    Data['id'] | { id: Data['id']; orgId?: string },
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    TagType,
    Data
  >
} & {
  [K in `${TagType}_create`]: MutationDefinition<
    Partial<Data>,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    TagType,
    Data
  >
} & {
  [K in `${TagType}_update`]: MutationDefinition<
    { id: Data['id'] } & Partial<Omit<Data, 'id'>>,
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    TagType,
    Data
  >
} & {
  [K in `${TagType}_delete`]: MutationDefinition<
    Data['id'],
    BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>,
    TagType,
    void
  >
}

export const createCrudEndpoints = <
  Data extends {
    id: string | number
  },
  B extends EndpointBuilder<any, any, any> = EndpointBuilder<any, any, any>,
  TagType extends TagTypes = TagTypes
>(
  tagType: TagType,
  builder: B
): CrudEndpoints<Data, TagType> => {
  return {
    // GET ALL
    [`${tagType}_getAll`]: builder.query<
      EntityState<Data, Data['id']>,
      { organisationId?: string } | void
    >({
      extraOptions: { isListWithEmpty404Response: true },
      query: (arg) => {
        return {
          url: `${tagType}`,
          // only add params if we actually have an org id
          params:
            arg && arg.organisationId
              ? { orgId: arg.organisationId }
              : undefined
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.ids.map((id) => ({ type: tagType, id })),
              { type: tagType, id: 'LIST' }
            ]
          : [{ type: tagType, id: 'LIST' }],
      transformResponse: (response: Data[]) => {
        const entityAdapter = createEntityAdapter<Data>()
        return entityAdapter.addMany(entityAdapter.getInitialState(), response)
      }
    }),

    // GET BY ID
    [`${tagType}_getById`]: builder.query<
      Data,
      Data['id'] | { id: Data['id']; orgId?: string }
    >({
      query: (arg) => {
        const id = typeof arg === 'object' && arg !== null ? arg.id : arg
        const orgId =
          typeof arg === 'object' && arg !== null ? arg.orgId : undefined
        return {
          url: `${tagType}/${id}`,
          params: orgId ? { orgId } : undefined
        }
      },
      providesTags: (result, error, arg) => {
        const id = typeof arg === 'object' && arg !== null ? arg.id : arg
        return [{ type: tagType, id }]
      }
    }),

    // CREATE
    [`${tagType}_create`]: builder.mutation<Data, Partial<Data>>({
      query: (body) => ({
        url: `${tagType}`,
        method: 'POST',
        body
      }),
      invalidatesTags: () => {
        return [{ type: tagType, id: 'LIST' }]
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        // Optimistic update can be handled here if needed
      }
    }),

    // UPDATE
    [`${tagType}_update`]: builder.mutation<
      Data,
      { id: Data['id'] } & Partial<Omit<Data, 'id'>>
    >({
      query: (body) => ({
        url: `${tagType}/${body.id}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: (result, error, arg) => {
        return [
          { type: tagType, id: 'LIST' },
          { type: tagType, id: result?.id || arg.id }
        ]
      },
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        // TODO: Optimistic update can be handled here if needed
      }
    }),

    // DELETE
    [`${tagType}_delete`]: builder.mutation<void, Data['id']>({
      query: (id) => ({
        url: `${tagType}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (result, error, data) => {
        return [
          { type: tagType, id: 'LIST' },
          { type: tagType, id: data }
        ]
      },
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        // TODO: Optimistic update can be handled here if needed
      }
    })
  } as CrudEndpoints<Data, TagType>
}
